import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { io } from 'socket.io-client';
import { useAuth } from '../context/AuthContext';
import { API_BASE, apiFetch, mediaUrl } from '../apiBase';
import CountUp from '../components/CountUp';

const pad = (n) => String(n).padStart(2, '0');

const PLATFORM_LABEL = { raingg: 'Rain.gg', betdice: 'BetDice', both: 'Rain.gg + BetDice' };

// How viewers earn the points raffles cost. `cmd` is highlighted like a chat command.
const HOW_TO = [
  { title: 'Be active in chat', body: 'Watch the stream on Kick and chat — active viewers earn points every minute.', icon: 'forum' },
  { title: 'Join rain', body: 'Rain drops through the stream; type', cmd: '!join', after: 'to grab part of the pot.', icon: 'thunderstorm' },
  { title: 'Predict', body: 'Bet your points on live predictions with', cmd: '!bet yes/no amount', after: '', icon: 'insights' },
];

const shortCount = (n) => (n >= 1000 ? `${(n / 1000).toFixed(n >= 10000 ? 0 : 1).replace(/\.0$/, '')}K` : String(n));

// "10:44 2026-10-30" in the viewer's own time zone.
function stamp(sec) {
  const d = new Date(sec * 1000);
  return `${pad(d.getHours())}:${pad(d.getMinutes())} ${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function SectionChip({ children }) {
  return <div className="rf-chip">{children}</div>;
}

function Stat({ label, title, children }) {
  return (
    <div className="rf-stat" title={title}>
      <div className="rf-stat-label">{label}</div>
      <div className="rf-stat-val">{children}</div>
    </div>
  );
}

/** One raffle as a compact portrait card — the same card for open and ended draws. */
function RaffleTile({ r, now, ended, onOpen, delay = 0 }) {
  const diff = Math.max(0, (r.ends_at || 0) * 1000 - now);
  const soon = !ended && diff < 172800000; // < 48h
  const req = r.required_platform ? PLATFORM_LABEL[r.required_platform] || r.required_platform : null;
  return (
    <article className={`rf-card scroll-reveal ${ended ? 'is-ended' : ''}`} style={{ transitionDelay: `${delay}ms` }}>
      {ended && <div className="rf-date">{stamp(r.closed_at || r.ends_at)}</div>}
      <div className="rf-image">
        <button type="button" className="rf-info" onClick={onOpen} title={ended ? 'View results' : 'Raffle details'}>
          <span className="material-symbols-outlined" style={{ fontSize: 15 }}>info</span>
        </button>
        {soon && <span className="rf-soon">Ending soon</span>}
        {r.image_url ? (
          <img src={mediaUrl(r.image_url)} alt={r.prize} />
        ) : (
          <span className="material-symbols-outlined rf-image-icon">redeem</span>
        )}
      </div>
      <div className="rf-prize" title={r.prize}>
        <span>{r.prize}</span>
        {r.points_prize > 0 && <span className="rf-prize-bonus">+ 🪙 {r.points_prize.toLocaleString()}</span>}
      </div>
      <div className="rf-stats">
        <Stat label="Req" title={req ? `${req} players only` : 'Open to everyone'}>
          <span className="material-symbols-outlined" style={{ fontSize: 15, color: req ? '#F6BB42' : '#7ef0a0' }}>{req ? 'lock' : 'lock_open'}</span>
        </Stat>
        <Stat label="Players">{r.entrantCount}</Stat>
        <Stat label="Tickets" title={`${r.ticketsSold.toLocaleString()} tickets`}>{shortCount(r.ticketsSold)}</Stat>
      </div>
      {ended ? (
        <button type="button" className="rf-results" onClick={onOpen} title="View results">
          <span className="material-symbols-outlined" style={{ fontSize: 15, color: '#F6BB42' }}>emoji_events</span>
          <span className="truncate">{r.winner_username || 'No winner'}</span>
          <span className="material-symbols-outlined rf-results-link" style={{ fontSize: 16 }}>chevron_right</span>
        </button>
      ) : (
        <>
          <div className="rf-timer">
            {[
              ['Day', Math.floor(diff / 86400000)],
              ['Hrs', Math.floor((diff % 86400000) / 3600000)],
              ['Min', Math.floor((diff % 3600000) / 60000)],
              ['Sec', Math.floor((diff % 60000) / 1000)],
            ].map(([lbl, val]) => (
              <div key={lbl} className="rf-time">
                <div className={`rf-time-val ${soon ? 'is-soon' : ''}`}>{pad(val)}</div>
                <div className="rf-time-label">{lbl}</div>
              </div>
            ))}
          </div>
          <button type="button" className="rf-join shine-hover" onClick={onOpen}>
            Join
            <span className="material-symbols-outlined" style={{ fontSize: 16 }}>login</span>
          </button>
        </>
      )}
    </article>
  );
}

function Avatar({ username, avatarUrl, size }) {
  const [broken, setBroken] = useState(false);
  const px = size + 'px';
  if (avatarUrl && !broken) {
    return (
      <img
        src={avatarUrl}
        alt={username}
        onError={() => setBroken(true)}
        className="rounded-full border border-[#2c2c38] flex-shrink-0 object-cover"
        style={{ width: px, height: px }}
      />
    );
  }
  return (
    <div
      className="rounded-full flex-shrink-0 flex items-center justify-center"
      style={{ width: px, height: px, background: 'linear-gradient(160deg,#242430,#14141c)', border: '1px solid #2c2c38' }}
    >
      <span className="font-display-lg font-black" style={{ color: '#ff544a', fontSize: Math.round(size * 0.4) }}>
        {username.charAt(0).toUpperCase()}
      </span>
    </div>
  );
}

export default function Raffles() {
  const { user, login, refresh } = useAuth();
  const [now, setNow] = useState(Date.now());
  const [raffles, setRaffles] = useState([]);
  const [history, setHistory] = useState([]);
  const [openId, setOpenId] = useState(null);
  const [entries, setEntries] = useState([]);
  const [qty, setQty] = useState(1);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  // The pop-up shows an open raffle (buy tickets) or an ended one (results).
  const openRaffle = raffles.find((r) => r.id === openId) || history.find((r) => r.id === openId);
  const openEnded = !!openRaffle && !raffles.some((r) => r.id === openId);

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  async function loadRaffles() {
    const res = await apiFetch('/api/raffles/active');
    setRaffles(await res.json());
  }
  async function loadHistory() {
    const res = await apiFetch('/api/raffles/history');
    setHistory(await res.json());
  }
  useEffect(() => {
    loadRaffles();
    loadHistory();
    const socket = io(API_BASE || undefined, { path: '/socket.io' });
    socket.on('raffle:update', () => {
      loadRaffles();
      loadHistory();
      if (openId) loadEntries(openId);
    });
    return () => socket.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function loadEntries(raffleId) {
    const res = await apiFetch(`/api/raffles/${raffleId}/entrants`);
    setEntries(await res.json());
  }

  function enterRaffle(id) {
    setOpenId(id);
    setQty(1);
    setError('');
    loadEntries(id);
  }

  async function buy() {
    if (!openRaffle) return;
    if (!user) {
      login();
      return;
    }
    setBusy(true);
    setError('');
    try {
      const res = await apiFetch(`/api/raffles/${openRaffle.id}/buy`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ qty }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Purchase failed');
      setRaffles((rs) => rs.map((r) => (r.id === json.id ? json : r)));
      await Promise.all([refresh(), loadEntries(openRaffle.id)]);
      setQty(1);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }

  let withRange = [];
  let runTotal = 0;
  if (openRaffle) {
    withRange = entries.map((e) => {
      const from = runTotal + 1;
      runTotal += e.count;
      return { ...e, from, to: runTotal };
    });
  }
  // A buyer can appear as several rows (one per purchase), so both of these
  // need to aggregate across rows rather than reading a single matching one.
  const myTickets = openRaffle
    ? entries.reduce((sum, e) => (e.username?.toLowerCase() === user?.username?.toLowerCase() ? sum + e.count : sum), 0)
    : 0;
  const uniqueEntrantCount = new Set(entries.map((e) => e.username?.toLowerCase())).size;
  // Results view: one row per player (their purchases summed), biggest share first.
  const byPlayer = Object.values(
    entries.reduce((acc, e) => {
      const k = e.username?.toLowerCase();
      acc[k] = acc[k] ? { ...acc[k], count: acc[k].count + e.count } : { ...e };
      return acc;
    }, {})
  ).sort((a, b) => b.count - a.count);
  const totalTickets = entries.reduce((sum, e) => sum + e.count, 0);

  const raffleDiff = openRaffle ? Math.max(0, (openRaffle.ends_at || 0) * 1000 - now) : 0;
  const raffleSoon = !openEnded && raffleDiff < 172800000; // < 48h, matches RaffleTile's own threshold

  const stats = openRaffle
    ? [
        { icon: 'confirmation_number', color: '#ff544a', label: 'Total Entries', numeric: openRaffle.ticketsSold },
        { icon: 'group', color: '#4a9dff', label: 'Total Users', numeric: uniqueEntrantCount },
        ...(openEnded ? [{ icon: 'emoji_events', color: '#F6BB42', label: 'Winner', val: openRaffle.winner_username || 'No winner' }] : []),
        ...(openRaffle.points_prize > 0
          ? [{ icon: 'redeem', color: '#F6BB42', label: 'Point Prize', numeric: openRaffle.points_prize, formatter: (n) => `🪙 ${Math.round(n).toLocaleString()}` }]
          : []),
        {
          icon: 'verified_user',
          color: openRaffle.required_platform ? '#F6BB42' : '#53fc18',
          label: 'Requirements',
          val: openRaffle.required_platform ? PLATFORM_LABEL[openRaffle.required_platform] || openRaffle.required_platform : 'None',
        },
      ]
    : [];

  return (
    <main className="max-w-[1240px] mx-auto px-5 py-12 relative z-10">
      <header className="scroll-reveal text-center mb-12">
        <h1 className="rf-title font-display-lg">Raffles</h1>
        <p className="font-title-md italic text-[#c9c9d6] mt-2" style={{ fontSize: 15 }}>
          <span className="text-[#ff7a52] font-bold">Weekly draws</span> — buy tickets with your points for a chance to win the prize!
        </p>
      </header>

      <SectionChip>Active raffles</SectionChip>
      {!raffles.length ? (
        <div className="rf-empty mb-14">
          <span className="material-symbols-outlined" style={{ fontSize: 26, color: '#4a4a58' }}>confirmation_number</span>
          No raffles open right now — check back soon.
        </div>
      ) : (
        <div className="rf-grid mb-14">
          {raffles.map((r, i) => (
            <RaffleTile key={r.id} r={r} now={now} onOpen={() => enterRaffle(r.id)} delay={i * 70} />
          ))}
        </div>
      )}

      <SectionChip>How to earn points?</SectionChip>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-14">
        {HOW_TO.map((h, i) => (
          <div key={h.title} className="rf-how scroll-reveal" style={{ transitionDelay: `${i * 70}ms` }}>
            <span className="material-symbols-outlined rf-how-icon">{h.icon}</span>
            <div className="min-w-0">
              <div className="font-title-md font-bold text-white" style={{ fontSize: 15 }}>{h.title}</div>
              <p className="font-title-md text-[#a5a5b4] mt-1" style={{ fontSize: 13, lineHeight: 1.5 }}>
                {h.body} {h.cmd && <span className="rf-cmd">{h.cmd}</span>} {h.after}
              </p>
            </div>
          </div>
        ))}
      </div>

      <SectionChip>Ended raffles</SectionChip>
      {!history.length ? (
        <div className="rf-empty">No raffles have ended yet.</div>
      ) : (
        <div className="rf-grid">
          {history.map((r, i) => (
            <RaffleTile key={r.id} r={r} now={now} ended onOpen={() => enterRaffle(r.id)} delay={(i % 5) * 60} />
          ))}
        </div>
      )}

      {openRaffle && createPortal(
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 sm:p-6" onClick={() => setOpenId(null)}>
          <div className="absolute inset-0" style={{ background: 'rgba(0,0,0,.85)' }} />
          <div onClick={(e) => e.stopPropagation()} style={{ background: '#0e0e14', border: '1px solid #24242e', maxWidth: 1140 }} className="relative rounded-2xl w-full max-h-[88vh] flex flex-col overflow-hidden">
            {/* Hero banner mirrors the RaffleCard's own image header, so opening
                the modal reads as "zooming into" the card you clicked rather
                than jumping to an unrelated generic dialog. */}
            <div
              className="relative flex items-center gap-5 px-6 sm:px-7 pt-6 sm:pt-7 pb-6 flex-shrink-0"
              style={{ background: 'radial-gradient(circle at 15% 20%,rgba(246,187,66,.14),transparent 60%),linear-gradient(160deg,#17171f,#0c0c12)', borderBottom: '1px solid #24242e' }}
            >
              <div style={{ position: 'absolute', inset: 0, opacity: 0.15, backgroundImage: 'radial-gradient(#2e2e3c 1px,transparent 1px)', backgroundSize: '14px 14px', pointerEvents: 'none' }} />
              <div
                className="relative flex items-center justify-center flex-shrink-0"
                style={{ width: 76, height: 76, borderRadius: 16, background: 'linear-gradient(160deg,#1e1e28,#0c0c12)', border: '1px solid #2a2a36' }}
              >
                {openRaffle.image_url ? (
                  <img src={mediaUrl(openRaffle.image_url)} alt={openRaffle.prize} className="max-w-[68%] max-h-[68%] object-contain" />
                ) : (
                  <span className="material-symbols-outlined" style={{ color: '#F6BB42', fontSize: 34 }}>redeem</span>
                )}
              </div>
              <div className="relative flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap mb-1.5">
                  {openEnded ? (
                    <span
                      className="font-mono-brand font-bold uppercase inline-flex items-center gap-1.5"
                      style={{ fontSize: 9, letterSpacing: '.18em', padding: '4px 9px', borderRadius: 999, color: '#a5a5b4', background: 'rgba(255,255,255,.05)', border: '1px solid #2c2c38' }}
                    >
                      Ended {stamp(openRaffle.closed_at || openRaffle.ends_at)}
                    </span>
                  ) : (
                    <span
                      className="font-mono-brand font-bold uppercase inline-flex items-center gap-1.5"
                      style={{ fontSize: 9, letterSpacing: '.18em', padding: '4px 9px', borderRadius: 999, color: raffleSoon ? '#ff8f86' : '#7ef0a0', background: raffleSoon ? 'rgba(255,84,74,.12)' : 'rgba(83,252,24,.1)', border: '1px solid ' + (raffleSoon ? 'rgba(255,84,74,.4)' : 'rgba(83,252,24,.3)') }}
                    >
                      <span style={{ width: 5, height: 5, borderRadius: '50%', background: raffleSoon ? '#ff544a' : '#53fc18' }} />
                      {raffleSoon ? 'Ending soon' : 'Live'}
                    </span>
                  )}
                  <span className="font-mono-brand" style={{ fontSize: 10, color: '#7d7d8c' }}>#{openRaffle.id}</span>
                </div>
                <div className="font-display-lg font-black text-white truncate" style={{ fontSize: 21 }}>{openRaffle.prize}</div>
                {openRaffle.points_prize > 0 && (
                  <div className="font-mono-brand font-bold flex items-center gap-1.5 mt-1" style={{ fontSize: 11, color: '#F6BB42' }}>
                    <span className="material-symbols-outlined" style={{ fontSize: 14 }}>redeem</span>
                    + 🪙 {openRaffle.points_prize.toLocaleString()} to the winner
                  </div>
                )}
              </div>
              <button onClick={() => setOpenId(null)} className="relative cursor-pointer leading-none flex-shrink-0 self-start" style={{ color: 'rgba(255,255,255,.5)', fontSize: 26, background: 'none', border: 'none' }}>×</button>
            </div>

            <div className="flex-1 overflow-y-auto px-6 sm:px-7 pt-7">
              {!openEnded && (
              <div className="mb-8" style={{ background: 'linear-gradient(135deg,#17171f,#101017)', border: '1px solid rgba(255,84,74,.25)', borderRadius: 16, padding: '20px 22px' }}>
                <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
                  <div className="font-mono-brand uppercase font-bold inline-flex items-center gap-2" style={{ fontSize: 10, letterSpacing: '.22em', color: '#ff544a' }}>
                    <span className="material-symbols-outlined" style={{ fontSize: 15 }}>confirmation_number</span>
                    Buy Tickets
                  </div>
                  <div className="font-mono-brand" style={{ fontSize: 12, color: '#888899' }}>
                    🪙 {openRaffle.ticket_cost.toLocaleString()} per ticket
                    {user ? (
                      <span style={{ color: '#F6BB42', fontWeight: 700 }}> · 🪙 <CountUp value={user.points} /> balance</span>
                    ) : (
                      <span style={{ color: '#7d7d8c' }}> · sign in to buy</span>
                    )}
                  </div>
                </div>
                <div className="flex items-center flex-wrap gap-3">
                  <div className="flex items-center" style={{ background: '#0a0a11', borderRadius: 10, overflow: 'hidden', flexShrink: 0 }}>
                    <button onClick={() => setQty((q) => Math.max(1, q - 1))} style={{ background: '#15151d', border: '1px solid #26263a', color: '#c9c9d6', width: 38, height: 42, fontSize: 18, cursor: 'pointer', borderRadius: '10px 0 0 10px' }}>−</button>
                    <input
                      type="number"
                      value={qty}
                      onChange={(e) => setQty(Math.max(1, Math.min(99, parseInt(e.target.value) || 1)))}
                      style={{ width: 62, height: 42, textAlign: 'center', background: '#0a0a11', border: '1px solid #26263a', borderLeft: 'none', borderRight: 'none', color: '#fff', fontFamily: 'monospace', fontWeight: 800, fontSize: 15 }}
                    />
                    <button onClick={() => setQty((q) => Math.min(99, q + 1))} style={{ background: '#15151d', border: '1px solid #26263a', color: '#c9c9d6', width: 38, height: 42, fontSize: 18, cursor: 'pointer', borderRadius: '0 10px 10px 0' }}>+</button>
                  </div>
                  {user && (
                    <button
                      onClick={() => setQty(Math.max(1, Math.min(99, Math.floor(user.points / openRaffle.ticket_cost) || 1)))}
                      className="font-mono-brand font-bold uppercase cursor-pointer flex-shrink-0"
                      style={{ fontSize: 10, letterSpacing: '.1em', color: '#c9c9d6', background: '#15151d', border: '1px solid #26263a', borderRadius: 8, padding: '0 14px', height: 42 }}
                    >
                      Max
                    </button>
                  )}
                  <div className="flex-1" style={{ minWidth: 8 }} />
                  {(() => {
                    const cost = qty * openRaffle.ticket_cost;
                    const lock = busy || (!!user && cost > user.points);
                    return (
                      <button
                        onClick={buy}
                        disabled={lock}
                        style={{ background: lock ? '#2a2a36' : 'linear-gradient(135deg,#ff6b5f,#ff3d31)', color: lock ? '#66667a' : '#12060a', borderRadius: 10, border: 'none', padding: '13px 22px', flexShrink: 0, cursor: lock ? 'not-allowed' : 'pointer' }}
                        className={`font-black uppercase flex items-center justify-center gap-2 transition-transform hover:scale-[1.02] active:scale-[0.98] ${lock ? '' : 'shine-hover'}`}
                      >
                        <span className="material-symbols-outlined" style={{ fontSize: 17 }}>confirmation_number</span>
                        <span style={{ fontSize: 12, letterSpacing: '.08em', whiteSpace: 'nowrap' }}>
                          {!user ? 'Sign in to buy' : busy ? 'Buying…' : `Buy for 🪙 ${cost.toLocaleString()}`}
                        </span>
                      </button>
                    );
                  })()}
                </div>
              </div>
              )}

              {error && <p className="text-sm text-[#ff544a] mb-6 text-center">{error}</p>}
              {myTickets > 0 && (
                <div className="font-mono-brand flex items-center gap-2 mb-8" style={{ fontSize: 12, color: '#7ef0a0', background: 'rgba(83,252,24,.07)', border: '1px solid rgba(83,252,24,.25)', borderRadius: 10, padding: '11px 14px' }}>
                  <span className="material-symbols-outlined" style={{ fontSize: 16 }}>check_circle</span>
                  <span>You {openEnded ? 'had' : 'own'} {myTickets} ticket{myTickets > 1 ? 's' : ''} in this raffle.</span>
                </div>
              )}

              <div className="grid gap-2.5 mb-9" style={{ gridTemplateColumns: 'repeat(auto-fit,minmax(172px,1fr))' }}>
                {stats.map((s) => (
                  <div key={s.label} style={{ background: '#15151d', border: '1px solid #23232e', borderRadius: 12, minHeight: 60 }} className="px-3.5 py-3 flex items-center gap-2.5">
                    <span className="material-symbols-outlined" style={{ color: s.color, fontSize: 22, flexShrink: 0 }}>{s.icon}</span>
                    <div className="min-w-0">
                      <div className="font-mono-brand uppercase truncate" style={{ fontSize: 8.5, letterSpacing: '.18em', color: '#7d7d8c', whiteSpace: 'nowrap' }}>{s.label}</div>
                      <div className="font-display-lg font-black text-white truncate" style={{ fontSize: 15, marginTop: 3 }}>
                        {s.numeric != null ? <CountUp value={s.numeric} formatter={s.formatter || ((n) => Math.round(n).toLocaleString())} /> : s.val}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="font-mono-brand uppercase font-bold" style={{ fontSize: 9, letterSpacing: '.25em', color: '#7d7d8c', marginBottom: 14 }}>
                {openEnded ? 'Players' : 'Entries'}
              </div>
              {openEnded ? (
                !byPlayer.length ? (
                  <div className="text-sm text-[#7d7d8c] pb-8">Nobody entered this raffle.</div>
                ) : (
                  <div className="flex flex-col gap-2 pb-8">
                    {byPlayer.map((e) => {
                      const won = !!openRaffle.winner_username && e.username?.toLowerCase() === openRaffle.winner_username.toLowerCase();
                      const share = totalTickets ? (e.count / totalTickets) * 100 : 0;
                      return (
                        <div key={e.username} className={`rf-result-row ${won ? 'is-winner' : ''}`}>
                          <Avatar username={e.username} avatarUrl={e.avatar_url} size={34} />
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2">
                              <span className="font-title-md font-bold text-white truncate" style={{ fontSize: 14 }}>{e.username}</span>
                              {won && <span className="rf-winner-tag"><span className="material-symbols-outlined" style={{ fontSize: 12 }}>emoji_events</span>Winner</span>}
                            </div>
                            <div className="rf-share"><i style={{ width: `${Math.max(1.5, share)}%` }} /></div>
                          </div>
                          <div className="text-right flex-shrink-0">
                            <div className="font-mono-brand font-bold text-white" style={{ fontSize: 13 }}>{e.count.toLocaleString()}</div>
                            <div className="font-mono-brand" style={{ fontSize: 10.5, color: '#7d7d8c' }}>{share < 0.1 ? '<0.1' : share.toFixed(1)}%</div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )
              ) : !withRange.length ? (
                <div className="text-sm text-[#7d7d8c] pb-8">No entries yet — be the first!</div>
              ) : (
                <div className="flex flex-wrap gap-3 pb-8">
                  {withRange.map((e) => {
                    const isMe = !!user && e.username?.toLowerCase() === user.username?.toLowerCase();
                    return (
                      <div
                        key={e.ticket_id}
                        style={{
                          background: isMe ? 'rgba(255,84,74,.08)' : '#15151d',
                          border: isMe ? '1px solid rgba(255,84,74,.55)' : '1px solid #23232e',
                          borderRadius: 14,
                          width: 128,
                          boxShadow: isMe ? '0 0 18px rgba(255,84,74,.18)' : 'none',
                        }}
                        className="relative px-3 py-4 flex flex-col items-center gap-2"
                      >
                        {isMe && (
                          <span
                            className="absolute font-mono-brand font-black uppercase"
                            style={{ top: -8, fontSize: 8, letterSpacing: '.1em', background: '#ff544a', color: '#fff', padding: '2px 8px', borderRadius: 999 }}
                          >
                            You
                          </span>
                        )}
                        <Avatar username={e.username} avatarUrl={e.avatar_url} size={52} />
                        <div className="font-title-md font-bold text-white text-center truncate" style={{ fontSize: 13, maxWidth: '100%' }}>{e.username}</div>
                        <div className="flex items-center gap-1">
                          <span className="material-symbols-outlined" style={{ color: '#F6BB42', fontSize: 15 }}>confirmation_number</span>
                          <span className="font-mono-brand font-bold text-white" style={{ fontSize: 12 }}>{e.count}</span>
                        </div>
                        <div className="font-mono-brand" style={{ color: '#7d7d8c', fontSize: 11 }}>{e.from} - {e.to}</div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            <div style={{ borderTop: '1px solid #1c1c24' }} className="px-6 sm:px-7 py-5 flex items-center justify-between gap-4 flex-wrap flex-shrink-0">
              <div className="font-mono-brand flex items-center gap-2" style={{ fontSize: 11, color: '#7d7d8c' }}>
                <span className="material-symbols-outlined" style={{ fontSize: 15 }}>shuffle</span>
                {openEnded ? 'The winner was picked at random when the timer ended.' : 'Winner is picked automatically and at random when the timer ends.'}
              </div>
              <button onClick={() => setOpenId(null)} style={{ background: '#b93a33', borderRadius: 8, border: 'none' }} className="text-white font-black uppercase tracking-wider cursor-pointer px-8 py-3 hover:opacity-90 transition-opacity flex-shrink-0">
                Close
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </main>
  );
}
